import React from 'react';
import { Group, Image, Text } from 'react-konva';
import { FurnitureShape as IFurnitureShape } from '../../../../types';
import { mergeRefs } from '../../../../utils/mergeRef';
import { DEFAULT_FURNITURE_FILL_COLOR } from '../../defines';
import { getTextProps } from '../../helpers/getTextProps';
import { getWindowKonvaObject } from '../../helpers/konva/getWindowKonvaObject';
import { ShapeCommonProps } from '../../types';
import { useShape } from '../ShapeContext/useShape';
import { FurnitureShapeSVG } from './FurnitureShapeSVG';

// Cache for storing raw SVG text to avoid re-fetching
const svgCache = new Map<string, string>();

function updateImageWithColor(
  svgText: string,
  color: string,
  setImage: (img: HTMLImageElement) => void
) {
  const coloredSvgText = svgText.replace(/currentColor/g, color);
  const blob = new Blob([coloredSvgText], { type: 'image/svg+xml' });
  const blobUrl = URL.createObjectURL(blob);

  const img = new window.Image();
  img.src = blobUrl;
  img.onload = () => {
    setImage(img);
    URL.revokeObjectURL(blobUrl);
    getWindowKonvaObject().stages?.[0]?.fire?.('contentChange');
  };
}

export interface FurnitureShapeComponentProps extends ShapeCommonProps {
  renderMode?: 'konva' | 'svg';
}

export const FurnitureShape = React.forwardRef<
  React.ElementRef<typeof Group>,
  FurnitureShapeComponentProps
>(({ renderMode = 'konva', ...commonProps }, ref) => {
  const { shape, image, setImage } = useShape();
  const furnitureShape = shape as IFurnitureShape;
  const shapeRef = React.useRef<React.ElementRef<typeof Group>>(null);
  const fillColor = shape.color || DEFAULT_FURNITURE_FILL_COLOR;
  const imgUrl = furnitureShape?.furniture?.url;

  React.useEffect(() => {
    if (!imgUrl) return;

    if (svgCache.has(imgUrl)) {
      // Use cached SVG text and generate the image with the new color
      const cachedSvg = svgCache.get(imgUrl)!;
      updateImageWithColor(cachedSvg, fillColor, setImage);
    } else {
      // Fetch and store the raw SVG text
      fetch(imgUrl)
        .then((response) => response.text())
        .then((svgText) => {
          svgCache.set(imgUrl, svgText); // Cache the raw SVG text
          updateImageWithColor(svgText, fillColor, setImage);
        })
        .catch((error) => console.error('Error loading SVG:', error));
    }
  }, [imgUrl]); // Fetch the SVG only once

  React.useEffect(() => {
    if (svgCache.has(imgUrl)) {
      // If the SVG is cached, just update the color without fetching
      const cachedSvg = svgCache.get(imgUrl)!;
      updateImageWithColor(cachedSvg, fillColor, setImage);
    }
  }, [fillColor]); // Update image when color changes

  React.useEffect(() => {
    if (shape.type === 'furniture' && image) {
      const layer = shapeRef.current?.getLayer();
      if (layer) {
        layer.batchDraw();
      }
    }
  }, [shape.type, image]);

  const { x, y, onTransformEnd, ...rest } = commonProps;

  // If renderMode is SVG, return SVG components
  if (renderMode === 'svg') {
    return <FurnitureShapeSVG />;
  }

  // Default Konva rendering
  return (
    <Group
      width={shape.width}
      height={shape.height}
      ref={mergeRefs([shapeRef, ref])}
      {...commonProps}
    >
      <Image
        image={image || undefined}
        width={shape.width}
        height={shape.height}
        opacity={shape.locked ? 0.5 : 1}
        imageSmoothingEnabled={true}
        perfectDrawEnabled={true}
      />
      {furnitureShape.text && (
        <Text
          text={furnitureShape.text}
          y={furnitureShape.height + 10}
          x={0}
          ref={(node) => {
            if (node) {
              const textWidth = node.width();
              if (furnitureShape.textAlign === 'center') {
                node.x((furnitureShape.width - textWidth) / 2);
              } else if (furnitureShape.textAlign === 'right') {
                node.x(furnitureShape.width - textWidth);
              } else {
                node.x(0);
              }
            }
          }}
          {...getTextProps(furnitureShape)}
          wrap={'none'}
        />
      )}
    </Group>
  );
});
